import { useState, useCallback, useEffect } from "react";
import { RotationMode } from "@onmo/graphql/graphql";
import { isDesktop, isIOS } from "react-device-detect";
import useEventListener from "./useEventListener";

type OrientationType =
  | "landscape-primary"
  | "landscape-secondary"
  | "portrait-primary"
  | "portrait-secondary";

const LANDSCAPE_ORIENTATIONS: OrientationType[] = [
  "landscape-primary",
  "landscape-secondary",
];
const PORTRAIT_ORIENTATIONS: OrientationType[] = [
  "portrait-primary",
  "portrait-secondary",
];

const getOrientationFromWindow = (): "landscape" | "portrait" => {
  return Math.abs(window.orientation as number) === 90
    ? "landscape"
    : "portrait";
};

const getOrientationFromScreen = (): "landscape" | "portrait" | null => {
  const type = window.screen?.orientation?.type as OrientationType | undefined;
  if (!type) return null;

  if (LANDSCAPE_ORIENTATIONS.includes(type)) return "landscape";
  if (PORTRAIT_ORIENTATIONS.includes(type)) return "portrait";
  return null;
};

const checkOrientationMatch = (
  currentOrientation: "landscape" | "portrait",
  expectedMode: RotationMode,
): boolean => {
  const expectedOrientation =
    expectedMode === RotationMode.Landscape ? "landscape" : "portrait";
  return currentOrientation === expectedOrientation;
};

const isOnRightRotationMode = (rotationMode?: RotationMode): boolean => {
  if (isDesktop || !rotationMode) return true;

  // iOS 16.4+ has issues with screen.orientation API, use window.orientation
  if (isIOS) {
    return checkOrientationMatch(getOrientationFromWindow(), rotationMode);
  }

  // Try modern Screen Orientation API first, fallback to window.orientation
  const screenOrientation = getOrientationFromScreen();
  const currentOrientation = screenOrientation ?? getOrientationFromWindow();

  return checkOrientationMatch(currentOrientation, rotationMode);
};

export function useRightRotation(orientation?: RotationMode): boolean {
  const [isRightRotation, setIsRightRotation] = useState(() =>
    isOnRightRotationMode(orientation),
  );

  const handleOrientationChange = useCallback(() => {
    setIsRightRotation(isOnRightRotationMode(orientation));
  }, [orientation]);

  // Re-check when orientation prop changes
  useEffect(() => {
    handleOrientationChange();
  }, [handleOrientationChange]);

  // Listen for orientation changes
  useEventListener("orientationchange", handleOrientationChange);

  // Modern browsers: also listen to screen.orientation.change
  useEffect(() => {
    const screenOrientation = window.screen?.orientation;
    if (!screenOrientation) return;

    screenOrientation.addEventListener("change", handleOrientationChange);
    return () => {
      screenOrientation.removeEventListener("change", handleOrientationChange);
    };
  }, [handleOrientationChange]);

  return isRightRotation;
}
